import React, { useState } from 'react';
import { FloatingPanel, PanelSection } from '../ui/Panel';
import { Button } from '../ui/Button';
import { Select, Checkbox } from '../ui/Input';
import { DUCT_TYPES, DUCT_SHAPES, DUCT_MATERIALS } from '../../constants/hvacOptions';

/**
 * DuctEditor - Editor panel for duct properties
 */
const DuctEditor = ({
  duct,
  onUpdate,
  onDelete,
  onClose,
  x = 20,
  y = 100,
  isMobile = false,
}) => {
  const [minimized, setMinimized] = useState(isMobile);

  if (!duct) return null;

  const currentDuctType = DUCT_TYPES[duct.type] || DUCT_TYPES.supply;
  const sizes = currentDuctType.sizes;

  // Calculate duct length
  const calculateLength = () => {
    if (!duct.points || duct.points.length < 2) return 0;
    let totalLength = 0;
    for (let i = 1; i < duct.points.length; i++) {
      const dx = duct.points[i].x - duct.points[i - 1].x;
      const dy = duct.points[i].y - duct.points[i - 1].y;
      totalLength += Math.sqrt(dx * dx + dy * dy);
    }
    return totalLength;
  };

  const lengthInches = calculateLength();
  const lengthFeet = Math.floor(lengthInches / 12);
  const remainingInches = Math.round(lengthInches % 12);

  const ductTypeOptions = Object.entries(DUCT_TYPES).map(([key, val]) => ({
    value: key,
    label: val.label,
  }));

  const ductShapeOptions = Object.entries(DUCT_SHAPES).map(([key, val]) => ({
    value: key,
    label: val.label,
  }));

  const materialOptions = DUCT_MATERIALS.map((mat) => ({
    value: mat.value,
    label: mat.label,
  }));

  const sizeOptions = sizes.map((size) => ({
    value: size.value,
    label: size.label,
  }));

  return (
    <FloatingPanel
      title="Duct Properties"
      onClose={onClose}
      onMinimize={() => setMinimized(!minimized)}
      minimized={minimized}
      isMobile={isMobile}
      x={x}
      y={y}
      maxHeight={400}
    >
      <div style={{ padding: '12px' }}>
        {/* Duct Type Info */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '12px',
            padding: '8px',
            background: `${currentDuctType.color}22`,
            borderRadius: '6px',
            border: `1px solid ${currentDuctType.color}44`,
          }}
        >
          <div
            style={{
              width: '24px',
              height: '24px',
              borderRadius: '4px',
              background: currentDuctType.color,
            }}
          />
          <div>
            <div style={{ fontWeight: '600', color: '#fff' }}>
              {currentDuctType.label} Duct
            </div>
            <div style={{ fontSize: '11px', color: '#8090a0' }}>
              {lengthFeet > 0 ? `${lengthFeet}'-${remainingInches}"` : `${remainingInches}"`} length
            </div>
          </div>
        </div>

        <PanelSection title="Type">
          <Select
            label="Duct Type"
            value={duct.type}
            onChange={(type) => {
              // Update size to default for new type
              const newType = DUCT_TYPES[type];
              onUpdate({ type, size: newType.defaultSize });
            }}
            options={ductTypeOptions}
          />
          <Select
            label="Shape"
            value={duct.ductShape || 'trunk'}
            onChange={(ductShape) => onUpdate({ ductShape })}
            options={ductShapeOptions}
          />
        </PanelSection>

        <PanelSection title="Size & Material">
          <Select
            label="Duct Size"
            value={duct.size}
            onChange={(size) => onUpdate({ size: Number(size) })}
            options={sizeOptions}
          />
          <Select
            label="Material"
            value={duct.material || 'sheet-metal'}
            onChange={(material) => onUpdate({ material })}
            options={materialOptions}
          />
          <Checkbox
            label="Insulated"
            checked={duct.insulated || false}
            onChange={(insulated) => onUpdate({ insulated })}
          />
        </PanelSection>

        <PanelSection title="Actions">
          <Button
            variant="danger"
            onClick={onDelete}
            style={{ width: '100%' }}
          >
            Delete Duct
          </Button>
        </PanelSection>
      </div>
    </FloatingPanel>
  );
};

export default DuctEditor;
